import React, { useState, useEffect, useRef } from 'react';
import { RiArrowDropDownLine, RiSearchLine, RiShoppingCartLine, RiMenuLine, RiUserLine } from "react-icons/ri";
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { RxCross2 } from "react-icons/rx";
import { RiArrowDownSLine } from "react-icons/ri";
import { isLoggedIn, getUserData, clearAuthData } from "../api/storageService";
import { toast } from "react-toastify";

const Navbar = () => {
  const [activeLink, setActiveLink] = useState('');
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [showApp, setShowApp] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const appDropdownRef = useRef(null);
  const userDropdownRef = useRef(null);

  // Auth states
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    // Check auth status on mount and route change
    setIsAuthenticated(isLoggedIn());
    setUserData(getUserData());
  }, [location]);

  useEffect(() => {
    if (location.pathname === '/') {
      setActiveLink('home');
    } else if (location.pathname === '/about-us') {
      setActiveLink('aboutUs');
    } else if (location.pathname === '/service') {
      setActiveLink('services');
    } else if (location.pathname === '/courses') {
      setActiveLink('courses');
    } else if (location.pathname === '/consult') {
      setActiveLink('consults');
    } else if (location.pathname === '/blog') {
      setActiveLink('blog');
    } else if (location.pathname === '/contact') {
      setActiveLink('contacts');
    } else if (location.pathname === '/login') {
      setActiveLink('login');
    } else if (location.pathname === '/register') {
      setActiveLink('register');
    } else if (location.pathname === '/dashboard') {
      setActiveLink('dashboard');
    }
  }, [location]);

  // Handle clicking outside the dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (appDropdownRef.current && !appDropdownRef.current.contains(event.target)) {
        setShowApp(false);
      }
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    if (showApp || showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showApp, showUserMenu]);

  const handleLinkClick = (link) => {
    setActiveLink(link);
    if (isMobileMenuOpen) {
      setMobileMenuOpen(false);
    }
  };

  const handleLogout = () => {
    clearAuthData();
    toast.success("Logged out successfully");
    setIsAuthenticated(false);
    setUserData(null);
    setShowUserMenu(false);
    navigate("/login");
  };

  const getInitials = (name) => {
    if (!name) return "U";

    const nameParts = name.trim().split(' ');

    if (nameParts.length === 1) {
      return nameParts[0].charAt(0).toUpperCase();
    }

    const firstInitial = nameParts[0].charAt(0).toUpperCase();
    const lastInitial = nameParts[nameParts.length - 1].charAt(0).toUpperCase();

    return firstInitial + lastInitial;
  };

  return (
    <nav className="custom-padding xl-screen-padding bg-white border-b shadow-sm sticky top-0 z-50">
      <div className="mx-auto py-2 flex flex-col md:flex-row justify-between items-center">
        <div className="flex items-center justify-between w-full md:w-auto">
          {/* Logo - Now on LEFT */}
          <Link to="/" className="flex items-center">
            <img
              src={require('../../src/assets/images/newlogo.png')}
              alt="Logo"
              className="h-auto w-[125px]"
            />
          </Link>

          {/* Hamburger Menu - Now on RIGHT */}
          <div className="md:hidden">
            {isMobileMenuOpen ? (
              <RxCross2
                onClick={() => setMobileMenuOpen(false)}
                className="text-2xl cursor-pointer"
              />
            ) : (
              <RiMenuLine
                onClick={() => setMobileMenuOpen(true)}
                className="text-2xl cursor-pointer"
              />
            )}
          </div>
        </div>

        {/* Mobile Menu - Opens from RIGHT */}
        <div className={`fixed inset-0 bg-black bg-opacity-60 z-40 md:hidden ${isMobileMenuOpen ? 'block' : 'hidden'}`} onClick={() => setMobileMenuOpen(false)}></div>

        <div className={`fixed top-0 right-0 h-full bg-white z-50 transform transition-transform duration-300 ease-in-out ${isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'} w-3/4`}>
          <div className="flex flex-col space-y-6 py-6 px-8">
            <div className="flex justify-between items-center">
              <Link
                to="/"
                className={`font-lato text-lg ${activeLink === 'home' ? 'text-[#6DBE45]' : 'text-gray-700'} font-semibold`}
                onClick={() => handleLinkClick('home')}
              >
                Home
              </Link>
              <RxCross2
                onClick={() => setMobileMenuOpen(false)}
                className="text-2xl cursor-pointer"
              />
            </div>

            <Link
              to="/about-us"
              className={`font-lato text-lg hover:text-[#6DBE45] ${activeLink === 'aboutUs' ? 'text-[#6DBE45]' : 'text-gray-700'} font-semibold`}
              onClick={() => handleLinkClick('aboutUs')}
            >
              About Us
            </Link>

            <Link
              to="/service"
              className={`font-lato text-lg hover:text-[#6DBE45] ${activeLink === 'services' ? 'text-[#6DBE45]' : 'text-gray-700'} font-semibold`}
              onClick={() => handleLinkClick('services')}
            >
              Services
            </Link>

            <Link
              to="/courses"
              className={`font-lato text-lg hover:text-[#6DBE45] ${activeLink === 'courses' ? 'text-[#6DBE45]' : 'text-gray-700'} font-semibold`}
              onClick={() => handleLinkClick('courses')}
            >
              Academy
            </Link>

            <Link
              to="/consult"
              className={`font-lato text-lg hover:text-[#6DBE45] ${activeLink === 'consults' ? 'text-[#6DBE45]' : 'text-gray-700'} font-semibold`}
              onClick={() => handleLinkClick('consults')}
            >
              Consult
            </Link>

            <Link
              to="/blog"
              className={`font-lato text-lg hover:text-[#6DBE45] ${activeLink === 'blog' ? 'text-[#6DBE45]' : 'text-gray-700'} font-semibold`}
              onClick={() => handleLinkClick('blog')}
            >
              Blog
            </Link>

            <Link
              to="/contact"
              className={`font-lato text-lg hover:text-[#6DBE45] ${activeLink === 'contacts' ? 'text-[#6DBE45]' : 'text-gray-700'} font-semibold`}
              onClick={() => handleLinkClick('contacts')}
            >
              Contact Us
            </Link>

            {isAuthenticated ? (
              // Show Dashboard and Logout when logged in (without name)
              <>
                <Link
                  to="/dashboard"
                  className={`font-lato text-lg hover:text-[#6DBE45] ${activeLink === 'dashboard' ? 'text-[#6DBE45]' : 'text-gray-700'} font-semibold`}
                  onClick={() => handleLinkClick('dashboard')}
                >
                  Dashboard
                </Link>
                <button
                  onClick={handleLogout}
                  className="font-lato text-lg text-left hover:text-red-600 text-gray-700 font-semibold"
                >
                  Logout
                </button>
              </>
            ) : (
              // Show Login and Register when not logged in
              <>
                <Link
                  to="/login"
                  className={`font-lato text-lg hover:text-[#6DBE45] ${activeLink === 'login' ? 'text-[#6DBE45]' : 'text-gray-700'} font-semibold`}
                  onClick={() => handleLinkClick('login')}
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  className={`font-lato text-lg hover:text-[#6DBE45] ${activeLink === 'register' ? 'text-[#6DBE45]' : 'text-gray-700'} font-semibold`}
                  onClick={() => handleLinkClick('register')}
                >
                  Register
                </Link>
              </>
            )}
          </div>
        </div>

        {/* Desktop Menu */}
        <div className="hidden md:flex md:w-auto md:flex-row md:space-x-8 items-center mt-4 md:mt-0">
          <Link
            to="/"
            className={`font-lato text-sm ${activeLink === 'home' ? 'text-[#6DBE45]' : 'text-gray-700'} font-semibold flex items-center`}
            onClick={() => handleLinkClick('home')}
          >
            Home
          </Link>

          <Link
            to="/about-us"
            className={`font-lato text-sm hover:text-[#6DBE45] ${activeLink === 'aboutUs' ? 'text-[#6DBE45]' : 'text-gray-700'} font-semibold flex items-center`}
            onClick={() => handleLinkClick('aboutUs')}
          >
            About Us
          </Link>

          <Link
            to="/service"
            className={`font-lato text-sm hover:text-[#6DBE45] ${activeLink === 'services' ? 'text-[#6DBE45]' : 'text-gray-700'} font-semibold flex items-center`}
            onClick={() => handleLinkClick('services')}
          >
            Services
          </Link>

          <Link
            to="/courses"
            className={`font-lato text-sm hover:text-[#6DBE45] ${activeLink === 'courses' ? 'text-[#6DBE45]' : 'text-gray-700'} font-semibold`}
            onClick={() => handleLinkClick('courses')}
          >
            Academy
          </Link>

          <Link
            to="/consult"
            className={`font-lato text-sm hover:text-[#6DBE45] ${activeLink === 'consults' ? 'text-[#6DBE45]' : 'text-gray-700'} font-semibold flex items-center`}
            onClick={() => handleLinkClick('consults')}
          >
            Consult
          </Link>

          <Link
            to="/blog"
            className={`font-lato text-sm hover:text-[#6DBE45] ${activeLink === 'blog' ? 'text-[#6DBE45]' : 'text-gray-700'} font-semibold flex items-center`}
            onClick={() => handleLinkClick('blog')}
          >
            Blogs
          </Link>

          <Link
            to="/contact"
            className={`font-lato text-sm hover:text-[#6DBE45] ${activeLink === 'contacts' ? 'text-[#6DBE45]' : 'text-gray-700'} font-semibold flex items-center`}
            onClick={() => handleLinkClick('contacts')}
          >
            Contact Us
          </Link>
        </div>

        <div className="flex space-x-4 items-center justify-end w-full md:w-auto md:mt-0 hidden md:flex">
          {/* User Icon with Dropdown */}
          <div className="relative" ref={userDropdownRef}>
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center justify-center bg-gray-100 rounded-full w-9 h-9 hover:bg-gray-200 transition-colors"
            >
              {isAuthenticated && userData ? (
                userData?.profile_img ? (
                  // Show profile image if available
                  <img
                    src={userData.profile_img}
                    alt="user"
                    className="w-9 h-9 rounded-full object-cover"
                  />
                ) : (
                  // Show first letter in circle if no image
                  <div className="w-9 h-9 rounded-full bg-[#5bb532] flex items-center justify-center text-white font-semibold text-sm">
                    {getInitials(userData?.fullName || userData?.name || "U")}
                  </div>
                )
              ) : (
                <RiUserLine className="text-lg text-gray-700" />
              )}
            </button>

            {showUserMenu && (
              <div className="absolute right-0 top-12 bg-white shadow-md rounded-lg py-2 w-48 z-50">
                {isAuthenticated ? (
                  // Logged in user menu
                  <>
                    {/* <div className="px-4 py-2 text-sm text-gray-500 border-b border-gray-100">
                      <span className="font-semibold text-gray-700">{userData?.name || "User"}</span>
                      <br />
                      <span className="text-xs">{userData?.role || "Customer"}</span>
                    </div> */}
                    <Link
                      to="/dashboard"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-[#6DBE45] transition-colors"
                      onClick={() => {
                        handleLinkClick('dashboard');
                        setShowUserMenu(false);
                      }}
                    >
                      Dashboard
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="block w-full text-left px-4 py-2 text-sm text-red-500 hover:bg-red-50 transition-colors"
                    >
                      Logout
                    </button>
                  </>
                ) : (
                  // Logged out user menu
                  <>
                    <Link
                      to="/login"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-[#6DBE45] transition-colors"
                      onClick={() => {
                        handleLinkClick('login');
                        setShowUserMenu(false);
                      }}
                    >
                      Login
                    </Link>
                    <Link
                      to="/register"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-[#6DBE45] transition-colors"
                      onClick={() => {
                        handleLinkClick('register');
                        setShowUserMenu(false);
                      }}
                    >
                      Register
                    </Link>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Download App */}
          <div className="relative" ref={appDropdownRef}>
            <button
              onClick={() => setShowApp(!showApp)}
              className="bg-[#5bb532] text-white px-4 py-2 text-sm font-semibold flex items-center gap-2 rounded-md hover:bg-[#4aa82e] transition-colors"
            >
              Download App
              <RiArrowDownSLine className={`text-lg transition-transform ${showApp ? 'rotate-180' : ''}`} />
            </button>

            {showApp && (
              <div className="absolute right-[-50px] top-12 bg-white shadow-md rounded-md p-3 w-[260px] z-50">
                <div className="flex gap-2 justify-center">
                  <a
                    href="https://play.google.com/store/apps/details?id=com.ankur1234567890.nutrillion"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      src={require('../../src/assets/images/Google play.webp')}
                      className="w-28"
                      alt="Google Play"
                    />
                  </a>

                  <a
                    href="https://apps.apple.com/in/app/nutrillion/id6761712657"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      src={require('../../src/assets/images/App Store.webp')}
                      className="w-28"
                      alt="App Store"
                    />
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;